import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, ArrowRight, Phone } from "@phosphor-icons/react";
import { api, formatPrice, formatDuration } from "@/lib/api";
import { TEXTURE_IMG, DEALS as STATIC_DEALS } from "@/data/content";
import { Skeleton } from "@/components/ui/skeleton";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/PhoneCTA";

// Display-name lookup for the curated US gateway origins we use as deal
// starting points. The destination city already comes from each deal's
// `title` field. Keeping the map small + hardcoded avoids an extra API call
// per page load.
const ORIGIN_CITIES = {
  JFK: "New York", LGA: "New York", EWR: "Newark",
  LAX: "Los Angeles", SFO: "San Francisco", SAN: "San Diego",
  ORD: "Chicago", MDW: "Chicago", BOS: "Boston", IAD: "Washington",
  DCA: "Washington", BWI: "Baltimore", MIA: "Miami", FLL: "Fort Lauderdale",
  SEA: "Seattle", PHX: "Phoenix", LAS: "Las Vegas", DFW: "Dallas",
  ATL: "Atlanta", DEN: "Denver", IAH: "Houston", PHL: "Philadelphia",
};

function cabinLabel(c) {
  if (!c) return "";
  return c.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function buildSearchLink(deal) {
  const params = new URLSearchParams({
    trip_type: "one_way",
    origin: deal.origin || "JFK",
    destination: deal.destination || "LAX",
    cabin: deal.cabin || "economy",
    ...(deal.depart_date ? { depart_date: deal.depart_date } : {}),
  });
  return `/search?${params.toString()}`;
}

// Format an ISO date string to "Aug 15, 2026"
function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
      month: "short", day: "2-digit", year: "numeric",
    });
  } catch {
    return iso;
  }
}

// Split a $123.45 price into [whole, cents] for the screenshot-style display.
function splitPrice(price, currency) {
  if (typeof price !== "number") return null;
  const parts = price.toFixed(2).split(".");
  return { whole: `$${Number(parts[0]).toLocaleString()}`, cents: parts[1], currency };
}

/** Mobile row — vertical text list matching the user's reference screenshot. */
function MobileDealRow({ deal }) {
  const originCity = ORIGIN_CITIES[deal.origin] || deal.origin;
  const destCity = deal.title || deal.destination;
  const price = splitPrice(deal.price, deal.currency || "USD");
  // The Deals endpoint returns a depart_date only — return_date is implicit
  // (we book one-way). Show only what we actually have to keep this honest.
  return (
    <Link
      to={buildSearchLink(deal)}
      data-testid={`deal-row-${deal.origin || "static"}-${deal.destination || "x"}`}
      className="flex items-center justify-between gap-4 px-5 py-4 bg-white border border-[#E0DDD6] hover:border-[#C29B62] transition-colors"
    >
      <div className="min-w-0">
        <p className="text-base font-semibold text-[#1C1C1C] leading-snug">
          <span className="text-[#666460] font-normal">From:</span> {originCity}
        </p>
        <p className="text-base font-semibold text-[#1C1C1C] leading-snug">
          <span className="text-[#666460] font-normal">To:</span> {destCity}
        </p>
        {deal.depart_date && (
          <p className="text-sm text-[#666460] mt-2">
            Depart: {fmtDate(deal.depart_date)}
          </p>
        )}
        {deal.airline_name && (
          <p className="text-xs text-[#A09E9A] mt-1 tracking-wide">
            {deal.airline_name} · {cabinLabel(deal.cabin)}
          </p>
        )}
      </div>
      {price ? (
        <p className="font-serif text-3xl tracking-tighter text-[#C29B62] whitespace-nowrap shrink-0">
          {price.whole}
          <sup className="text-base align-super ml-0.5">.{price.cents}</sup>
        </p>
      ) : (
        <p className="text-xs text-[#666460] uppercase tracking-wider shrink-0">Call for fare</p>
      )}
    </Link>
  );
}

/** Desktop card (kept identical to the prior carousel card). */
function DealCard({ deal }) {
  const hasLive = !!deal.price;
  return (
    <Link
      to={buildSearchLink(deal)}
      data-testid={`deal-${deal.origin || "static"}-${deal.destination || "x"}`}
      className="group relative bg-[#141415] border border-white/10 overflow-hidden lift"
    >
      <div className="aspect-[16/10] overflow-hidden relative">
        <img src={deal.img} alt={deal.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute top-4 left-4 px-3 py-1 bg-[#C29B62] text-[#0A0A0B] tcp-overline">{deal.badge}</span>
        {hasLive && (
          <span className="absolute top-4 right-4 px-2 py-1 bg-[#0A0A0B]/70 text-[#C29B62] text-[10px] tracking-[0.18em] uppercase font-semibold backdrop-blur" data-testid="deal-live-tag">
            Live fare
          </span>
        )}
      </div>
      <div className="p-5">
        <p className="tcp-overline text-[#C29B62] mb-1.5">
          {cabinLabel(deal.cabin)}{deal.airline_name ? ` · ${deal.airline_name}` : ""}
        </p>
        <h3 className="font-serif text-xl tracking-tight mb-2">{deal.title}</h3>
        <div className="flex items-end justify-between pt-3 border-t border-white/10">
          <div>
            <p className="text-xs text-[#A09E9A]">From</p>
            <p className="font-serif text-2xl" style={{ color: "#C29B62" }}>
              {hasLive ? formatPrice(deal.price, deal.currency || "USD") : "Coming soon"}
            </p>
          </div>
          <p className="text-xs text-[#A09E9A]">
            {deal.duration_minutes ? formatDuration(deal.duration_minutes) : deal.nights}
          </p>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-[#A09E9A] tracking-wider">
          <span>{deal.origin} → {deal.destination}</span>
          <ArrowRight size={14} className="text-[#C29B62] -translate-x-1 group-hover:translate-x-0 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

export default function Deals() {
  const [deals, setDeals] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/products/flights/deals")
      .then((r) => {
        if (!alive) return;
        const live = (r.data?.deals || []);
        const filtered = live.length ? live : STATIC_DEALS;
        setDeals(filtered);
        setRefreshedAt(r.data?.refreshed_at || null);
      })
      .catch(() => alive && setDeals(STATIC_DEALS));
    return () => { alive = false; };
  }, []);

  return (
    <>
      {/* ============ MOBILE — CheapOair-style "Don't Miss" vertical list ============ */}
      <section
        className="md:hidden bg-white px-5 py-10 border-t border-[#E0DDD6]"
        data-testid="deals-section-mobile"
      >
        <h2 className="font-serif text-3xl tracking-tighter text-[#1C1C1C] leading-tight">
          Don't miss these <em className="italic" style={{ color: "#C29B62" }}>fares</em>
        </h2>
        <a
          href={`tel:${PHONE_TEL}`}
          className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#666460] hover:text-[#C29B62] transition-colors whitespace-nowrap"
          data-testid="deals-mobile-phone-link"
        >
          <Phone size={14} weight="fill" className="text-[#C29B62] shrink-0" />
          <span>
            Phone-only deals:{" "}
            <span className="font-semibold text-[#C29B62]">{PHONE_DISPLAY}</span>
          </span>
        </a>

        <div className="mt-5 space-y-3">
          {!deals && [1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-[108px] w-full bg-[#F7F5F0]" />
          ))}
          {deals && deals.map((d, i) => (
            <MobileDealRow key={`${d.origin || "s"}-${d.destination || i}`} deal={d} />
          ))}
        </div>

        {refreshedAt && (
          <p className="text-[11px] text-[#A09E9A] mt-5 tracking-wider" data-testid="deals-refreshed-mobile">
            Rates last found {new Date(refreshedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
          </p>
        )}
      </section>

      {/* ============ DESKTOP — unchanged carousel/grid ============ */}
      <section
        className="hidden md:block relative py-12 md:py-20 px-5 md:px-12 lg:px-24 text-[#F7F5F0]"
        style={{
          backgroundImage: `linear-gradient(rgba(10,10,11,0.94), rgba(10,10,11,0.96)), url(${TEXTURE_IMG})`,
          backgroundSize: "cover",
        }}
        data-testid="deals-section"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6 md:mb-8">
          <div>
            <p className="tcp-overline text-[#C29B62] mb-3 md:mb-4 flex items-center gap-2">
              <Sparkle size={14} weight="fill" /> Live Flight Deals
            </p>
            <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">
              Real <em className="italic" style={{ color: "#C29B62" }}>fares</em>. Refreshed every six hours.
            </h2>
          </div>
          <div className="md:text-right md:max-w-md">
            <p className="text-[#A09E9A] text-sm md:text-base">
              Cheapest live fare across our curated premium routes, pulled directly from our airline partners.
            </p>
            {refreshedAt && (
              <p className="text-xs text-[#A09E9A] mt-2 tracking-wider" data-testid="deals-refreshed">
                Last refreshed {new Date(refreshedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {!deals && [1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[420px] w-full bg-[#141415]" />
          ))}
          {deals && deals.map((d, i) => (
            <DealCard key={`${d.origin || "s"}-${d.destination || i}`} deal={d} />
          ))}
        </div>
      </section>
    </>
  );
}
